import { lazy, Suspense } from 'react';
import { CONTENT_TEST_ARTIFACT_KEY_PREFIX } from '@inference.engine/widgets/web';
import { Spinner } from '@monksflow/monks-ui';
import { useContentTest } from '@/entities/content-test';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';

// ResultsReport pulls Recharts into its module graph. Lazy-load so the Library
// bundle only ships it when a content-test artifact is actually previewed
// (mirrors ContentTest/Detail.tsx).
const ResultsReport = lazy(() =>
  import('@/pages/ContentTest/components/ResultsReport').then((m) => ({
    default: m.ResultsReport,
  })),
);

interface Props {
  artifactKey: string;
}

function PreviewSpinner() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <Spinner />
    </div>
  );
}

export function ContentTestArtifactPreview({ artifactKey }: Props) {
  const contentTestId = artifactKey.startsWith(CONTENT_TEST_ARTIFACT_KEY_PREFIX)
    ? artifactKey.slice(CONTENT_TEST_ARTIFACT_KEY_PREFIX.length)
    : null;

  const query = useContentTest(contentTestId);

  if (!contentTestId) return <ArtifactPreviewFallback />;

  if (query.isPending) {
    return <PreviewSpinner />;
  }

  if (query.isError || !query.data) {
    return <ArtifactPreviewFallback />;
  }

  // Reuse the canonical content-test report (the same view the detail page
  // renders) so the preview matches the test content exactly. `readOnly` hides
  // the report's own header/actions — the preview modal supplies its own chrome.
  return (
    <Suspense fallback={<PreviewSpinner />}>
      <ResultsReport test={query.data} readOnly />
    </Suspense>
  );
}
